/* eslint-disable new-cap */
import React, { createRef } from "react";
import PropTypes from "prop-types";
import d3KitGridmap from "d3kit-gridmap";
import { throttle } from "throttle-debounce";
import { colorFillScale, colorTextScale } from "../../utils/scaleTileGrid";
import {
  MAP_MAX_WIDTH,
  MAP_MIN_WIDTH,
  MID_BREAKPOINT,
  MAX_FONT_SIZE,
  MIN_FONT_SIZE,
} from "../../utils/constantsTileGrid";
import { pymSendHeight } from "../../utils/handlePym";
import Tooltip from "./Tooltip";

class GridMap extends React.Component {
  constructor(props) {
    super(props);
    this.refMap = createRef();

    /* We use throttle to minimize the number of times we check window size
    this is important for performance
    */
    this.throttledHandleWindowResize = throttle(300, this.resizeMap);
    this.state = {
      tooltipActive: false,
      tooltipData: null,
    };
  }

  componentDidMount() {
    this.chart = this.initMap();
    window.addEventListener("resize", this.throttledHandleWindowResize);
    pymSendHeight();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.throttledHandleWindowResize);
  }

  handleWidth = () => {
    const winWidth = window.innerWidth;
    let width;
    if (winWidth > MAP_MAX_WIDTH) {
      width = MAP_MAX_WIDTH;
    } else if (winWidth < MAP_MIN_WIDTH) {
      width = MAP_MIN_WIDTH;
    } else {
      width = winWidth;
    }
    width *= 0.9;
    const tileWidth = width / 12;
    return { width, tileWidth };
  };

  resizeMap = () => {
    const { tileWidth } = this.handleWidth();
    this.chart
      .options({
        tileWidth,
        tileHeight: tileWidth,
        margin: { left: 5, right: 5 },
        textSize: this.handleFontSize(),
      })
      .resizeToFitMap();
  };

  handleFontSize = () => {
    const winWidth = window.innerWidth;
    let fontSize;
    if (winWidth < MAP_MIN_WIDTH) {
      fontSize = MIN_FONT_SIZE;
    } else if (winWidth > MAP_MIN_WIDTH && winWidth < MID_BREAKPOINT) {
      const range = MID_BREAKPOINT - MAP_MIN_WIDTH;
      const rangeUnit = (MAX_FONT_SIZE - MIN_FONT_SIZE) / range;
      fontSize = MIN_FONT_SIZE + rangeUnit * (winWidth - MAP_MIN_WIDTH);
    } else {
      fontSize = MAX_FONT_SIZE;
    }
    return `${fontSize}px`;
    // return winWidth < MID_BREAKPOINT ? "12px" : "16px"
  };

  initMap() {
    const { data, colorScaleField } = this.props;
    const { width, tileWidth } = this.handleWidth();
    return new d3KitGridmap(this.refMap.current, {
      col: (d) => d.x,
      row: (d) => d.y,
      fill: (d) => colorFillScale(d[colorScaleField]),
      textColor: (d) => colorTextScale(d[colorScaleField]),
      stroke: "white",
      initialWidth: width,
      tileWidth,
      tileHeight: tileWidth,
      textSize: this.handleFontSize(),
      margin: { left: 5, right: 5 },
    })
      .data(data)
      .resizeToFitMap()
      .on("tileMouseover", (d) => {
        this.setState({
          tooltipActive: true,
          tooltipData: d,
        });
      })
      .on("tileMouseout", (d) => {
        this.setState({
          tooltipActive: false,
          tooltipData: d,
        });
      });
  }

  render() {
    const { tooltipActive, tooltipData } = this.state;
    const {
      tooltipLabelField,
      tooltipValueField,
      tooltipValueFormatter,
    } = this.props;
    return (
      <div>
        <Tooltip
          active={tooltipActive}
          data={tooltipData}
          labelField={tooltipLabelField}
          valueField={tooltipValueField}
          valueFormatter={tooltipValueFormatter}
        />
        <div ref={this.refMap} />
      </div>
    );
  }
}

GridMap.defaultProps = {
  tooltipValueFormatter: null,
};

GridMap.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  tooltipLabelField: PropTypes.string.isRequired,
  tooltipValueField: PropTypes.string.isRequired,
  tooltipValueFormatter: PropTypes.func,
  colorScaleField: PropTypes.string.isRequired,
};

export default GridMap;
